import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { RiFileExcel2Line } from 'react-icons/ri';
import FileSaver from 'file-saver';
import { useSnackbar } from 'notistack';
import { PrebuiltSettings, selectedMembership, useAxios } from '@intly/core';
import DialogTitle from '../../Dialog/DialogTitle';
import Banner from '../../../ui/Banner/Banner';
import {
  BOOKING_DATE_COLUMN,
  CATEGORY_COLUMN,
  COST_CENTER_REVIEW,
  CostCenterReviewTransactions,
  PAY_GROUP_COLUMN,
  ReviewCell,
} from './costCenterReview.types';
import { describeRow } from './reviewDerived';
import { tableSx } from './reviewTableStyles';
import {
  buildTransactionsWorkbook,
  transactionColumnFormat,
} from './transactionsExport';

interface ReviewTransactionsDialogProps {
  cell: ReviewCell | null;
  onClose: () => void;
  companyId: string;
  companyName: string;
  costCenterColumn: string | null;
  costCenter: string | null;
  costCenterLabel: string;
  settings: PrebuiltSettings;
}

const amountFormat = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const quantityFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 2,
});

// "Expandera": every posting behind one review cell (or, from "Expandera per anställd", one
// employee's), with its total (which equals the cell / the employee's line) and an Excel
// export of the same columns.
const ReviewTransactionsDialog: FC<ReviewTransactionsDialogProps> = ({
  cell,
  onClose,
  companyId,
  companyName,
  costCenterColumn,
  costCenter,
  costCenterLabel,
  settings,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { backendDataRequest } = useAxios();
  const groupId = useReactiveVar(selectedMembership);
  const [exporting, setExporting] = useState(false);

  const account = cell?.row.account;
  // An account row covers every category; a detail row one (possibly none, '').
  const category =
    cell?.row.kind === 'detail' ? cell.row.description ?? '' : undefined;
  const employee = cell?.employee;

  const query = useQuery<CostCenterReviewTransactions>({
    queryKey: [
      'prebuilt',
      COST_CENTER_REVIEW,
      'rows',
      groupId,
      companyId,
      costCenter,
      cell?.period,
      account,
      category,
      employee?.number,
      settings,
    ],
    enabled: Boolean(cell && groupId),
    staleTime: Infinity,
    queryFn: async () => {
      const response = await backendDataRequest('data', {
        method: 'POST',
        data: {
          function: 'prebuilt_dashboard_rows',
          groupId,
          dashboard: COST_CENTER_REVIEW,
          companyId,
          period: cell?.period,
          costCenterColumn,
          costCenter,
          cell: { account, category, employee: employee?.number },
          settings,
        },
      });
      if (!response?.data || response.data.error) {
        throw new Error(response?.data?.error ?? 'No result');
      }
      return response.data as CostCenterReviewTransactions;
    },
  });

  const columnLabel = (key: string, label: string) =>
    key === PAY_GROUP_COLUMN
      ? t('PAY_GROUP')
      : key === BOOKING_DATE_COLUMN
      ? t('BOOKING_DATE')
      : key === CATEGORY_COLUMN && label === key
      ? t('PAYCODE_CATEGORY')
      : label;

  const rowLabel = cell
    ? cell.row.kind === 'account'
      ? describeRow(cell.row)
      : `${cell.row.account} · ${describeRow(cell.row)}`
    : '';
  const employeeLabel = employee
    ? [employee.number, employee.name].filter(Boolean).join(' ') ||
      t('REVIEW_NO_EMPLOYEE')
    : '';
  const title = cell
    ? `${t('REVIEW_TRANSACTIONS')} ${cell.row.account} ${cell.period}${
        employee ? ` · ${employeeLabel}` : ''
      }`
    : '';
  const subtitle = `${t('COMPANY')}: ${companyName} · ${t(
    'COST_CENTER'
  )}: ${costCenterLabel} · ${rowLabel}`;

  const data = query.data;
  const formats =
    data?.columns.map(({ key }) =>
      transactionColumnFormat(key, data.amountColumn)
    ) ?? [];
  const amountIndex =
    data?.columns.findIndex(({ key }) => key === data.amountColumn) ?? -1;

  const display = (value: unknown, index: number) => {
    if (value === null || value === undefined) {
      return '';
    }
    const format = formats[index];
    if (format !== 'text') {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(n)) {
        return format === 'amount'
          ? amountFormat.format(n)
          : quantityFormat.format(n);
      }
    }
    return String(value);
  };

  const handleExport = async () => {
    if (!data || !cell) {
      return;
    }
    setExporting(true);
    try {
      const workbook = buildTransactionsWorkbook({
        title,
        subtitle,
        sheetName: `${cell.row.account} ${cell.period}`,
        transactions: data,
        columnLabel,
        totalLabel: t('REVIEW_TOTAL'),
      });
      const response = await backendDataRequest('data', {
        method: 'POST',
        data: {
          function: 'prebuilt_dashboard_export',
          groupId,
          dashboard: COST_CENTER_REVIEW,
          workbook,
        },
      });
      const url = response?.data?.result?.url;
      if (!url) {
        throw new Error(response?.data?.error ?? 'No file');
      }
      FileSaver.saveAs(url);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('EXPORT_TO_EXCEL_FAILED') as string, {
        variant: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="xl" open={Boolean(cell)} onClose={onClose}>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {subtitle}
            {data &&
              ` · ${t('REVIEW_TRANSACTIONS_ROWS', {
                count: data.rows.length,
              })}`}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              exporting ? <CircularProgress size={16} /> : <RiFileExcel2Line />
            }
            disabled={exporting || !data?.rows.length}
            onClick={handleExport}
          >
            {t('EXPORT_TO_EXCEL')}
          </Button>
        </Box>

        {query.isError && (
          <Banner
            severity="error"
            message={`${t('REVIEW_TRANSACTIONS_LOAD_ERROR')}: ${
              (query.error as Error).message
            }`}
          />
        )}
        {data?.truncated && (
          <Banner
            severity="warning"
            message={t('REVIEW_TRANSACTIONS_TRUNCATED') as string}
          />
        )}
        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {data && !data.rows.length && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            {t('REVIEW_TRANSACTIONS_EMPTY')}
          </Typography>
        )}
        {data && data.rows.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 'calc(100vh - 260px)' }}
          >
            <Table stickyHeader size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  {data.columns.map(({ key, label }, i) => (
                    <TableCell
                      key={key}
                      align={formats[i] === 'text' ? 'left' : 'right'}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {columnLabel(key, label)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row, r) => (
                  <TableRow key={r} hover>
                    {row.map((value, i) => (
                      <TableCell
                        key={data.columns[i]?.key ?? i}
                        align={formats[i] === 'text' ? 'left' : 'right'}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {display(value, i)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'background.paper',
                  '& .MuiTableCell-root': {
                    fontWeight: 'bold',
                    color: 'text.primary',
                  },
                }}
              >
                <TableRow>
                  {data.columns.map(({ key }, i) => (
                    <TableCell
                      key={key}
                      align={formats[i] === 'text' ? 'left' : 'right'}
                    >
                      {i === 0
                        ? t('REVIEW_TOTAL')
                        : i === amountIndex
                        ? amountFormat.format(data.total)
                        : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewTransactionsDialog;
